import { useMemo } from 'react'
import { parseTraceData, type TraceNode } from '@/lib/utils/traceParser'


interface LatencyWaterfallProps {
    data: any
    onNodeSelect?: (node: TraceNode) => void
    selectedNodeId?: string
}

interface WaterfallItem {
    node: TraceNode
    startOffset: number
    duration: number
    level: number
}

export function LatencyWaterfall({ data, onNodeSelect, selectedNodeId }: LatencyWaterfallProps) {
    const { items, totalDuration } = useMemo(() => {
        const root = parseTraceData(data)
        if (!root) return { items: [], totalDuration: 0 }

        const list: WaterfallItem[] = []
        const rootStart = root.startTime

        // Depth-first traversal to flatten
        const traverse = (node: TraceNode, level: number) => {
            const startOffset = node.startTime - rootStart
            const duration = node.endTime
                ? (node.endTime - node.startTime)
                : (node.latency || 0)

            list.push({
                node,
                startOffset: startOffset < 0 ? 0 : startOffset, // Safety calc
                duration: duration < 0 ? 0 : duration,
                level
            })

            if (node.children) {
                node.children.forEach(c => traverse(c, level + 1))
            }
        }

        traverse(root, 0)

        // Calculate max extent for scaling
        const maxEnd = list.reduce((max, item) => Math.max(max, item.startOffset + item.duration), 0)

        return { items: list, totalDuration: maxEnd || 1 }
    }, [data])

    if (items.length === 0) return null

    return (
        <div className="w-full flex flex-col gap-1 pb-4">
            <div className="flex justify-between text-xs text-gray-500 font-mono mb-2 border-b border-white/10 pb-1">
                <span>0ms</span>
                <span>{totalDuration.toFixed(0)}ms</span>
            </div>

            {items.map((item) => {
                const isSelected = selectedNodeId === item.node.id

                // Calculate percentages
                const leftPct = (item.startOffset / totalDuration) * 100
                const widthPct = Math.max((item.duration / totalDuration) * 100, 0.5) // Min width 0.5%

                // Color based on type
                let barColor = 'bg-gray-600'
                if (item.node.type === 'llm') barColor = 'bg-purple-500'
                if (item.node.type === 'retriever') barColor = 'bg-blue-500'
                if (item.node.type === 'tool') barColor = 'bg-orange-500'
                if (item.node.status === 'error') barColor = 'bg-red-500'

                return (
                    <div
                        key={item.node.id}
                        className={`group flex items-center gap-2 hover:bg-white/5 rounded px-2 py-1 cursor-pointer transition-colors ${isSelected ? 'bg-white/10 ring-1 ring-blue-500/50' : ''}`}
                        onClick={() => onNodeSelect?.(item.node)}
                    >
                        {/* Label */}
                        <div className="w-1/4 min-w-[150px] overflow-hidden">
                            <div className="truncate text-xs font-medium text-gray-300" style={{ paddingLeft: `${item.level * 12}px` }}>
                                {item.node.name}
                            </div>
                        </div>

                        {/* Bar Container */}
                        <div className="flex-1 relative h-6 bg-white/5 rounded overflow-hidden">
                            {/* Bar */}
                            <div
                                className={`absolute top-1 bottom-1 rounded-sm ${barColor} opacity-80 group-hover:opacity-100 transition-all`}
                                style={{
                                    left: `${leftPct}%`,
                                    width: `${widthPct}%`
                                }}
                            />
                            {/* Tooltip on hover (simple title for now) */}
                            <div
                                className="absolute top-1/2 -translate-y-1/2 text-[10px] text-white/70 ml-2 whitespace-nowrap pointer-events-none"
                                style={{ left: `${Math.min(leftPct + widthPct, 95)}%` }}
                            >
                                {item.duration.toFixed(0)}ms
                            </div>
                        </div>
                    </div>
                )
            })}

            {/* Legend */}
            <div className="flex gap-4 mt-6 text-[10px] text-gray-500 justify-center border-t border-white/10 pt-4">
                <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-purple-500"></div>LLM</div>
                <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-blue-500"></div>Retriever</div>
                <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-orange-500"></div>Tool</div>
                <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-gray-600"></div>Chain/Other</div>
                <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-red-500"></div>Error</div>
            </div>
        </div>
    )
}
